import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { TrashIcon, PlusIcon } from '@phosphor-icons/react';
import {
  DimensionType,
  DimensionGroupingConfig,
  DimensionGroup,
  DIMENSION_LABELS,
  createDefaultCatchAllGroups,
} from '@/types/rebateAnalytics';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';

interface DimensionGroupingEditorProps {
  dimensionType: Exclude<
    DimensionType,
    DimensionType.TIME | DimensionType.RULE_TYPE
  >;
  itemIdToName: Map<string, string>;
  groupingConfig: DimensionGroupingConfig;
  onConfigChange: (config: DimensionGroupingConfig | undefined) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface GroupCardProps {
  group: DimensionGroup;
  allOptions: MultiSelectOption[];
  itemIdToName: Map<string, string>;
  onUpdateItems: (groupId: string, itemIds: string[]) => void;
  onRenameGroup: (groupId: string, newName: string) => void;
  onDeleteGroup: (groupId: string) => void;
  dimensionType: Exclude<
    DimensionType,
    DimensionType.TIME | DimensionType.RULE_TYPE
  >;
  isCatchAll?: boolean; // Whether this is a catch-all group (immutable structure)
}

function GroupCard({
  group,
  allOptions,
  itemIdToName,
  onUpdateItems,
  onRenameGroup,
  onDeleteGroup,
  dimensionType,
  isCatchAll = false,
}: GroupCardProps) {
  const [draftName, setDraftName] = useState<string | null>(null);

  const displayName = draftName ?? group.name;

  const handleSaveName = useCallback(() => {
    if (draftName !== null) {
      if (draftName.trim() && draftName.trim() !== group.name) {
        onRenameGroup(group.id, draftName.trim());
      }
      setDraftName(null);
    }
  }, [draftName, group.id, group.name, onRenameGroup]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDraftName(e.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSaveName();
        e.currentTarget.blur();
      } else if (e.key === 'Escape') {
        setDraftName(null);
        e.currentTarget.blur();
      }
    },
    [handleSaveName],
  );

  // Convert group.itemIds to MultiSelectOption[]
  const selectedOptions = useMemo(() => {
    return allOptions.filter((opt) => group.itemIds.includes(opt.id));
  }, [allOptions, group.itemIds]);

  // Handle MultiSelect change
  const handleSelectionChange = useCallback(
    (selected: MultiSelectOption[]) => {
      const itemIds = selected.map((opt) => opt.id);
      onUpdateItems(group.id, itemIds);
    },
    [group.id, onUpdateItems],
  );

  const getItemDisplayName = useCallback(
    (itemId: string): string => {
      return itemIdToName.get(itemId) || itemId;
    },
    [itemIdToName],
  );

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <Input
            value={displayName}
            onChange={handleChange}
            onBlur={handleSaveName}
            onKeyDown={handleKeyDown}
            className="h-8 text-sm font-medium"
          />
          {!isCatchAll && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteGroup(group.id);
              }}
              className="h-6 w-6 p-0"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <TrashIcon className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {isCatchAll ? (
          // Catch-all groups don't have selectable items - they catch everything else
          <div className="py-2 text-sm text-muted-foreground">
            This group automatically includes all items that don&apos;t match
            any user groups.
          </div>
        ) : (
          <>
            {/* MultiSelect for choosing items */}
            <MultiSelect
              options={allOptions}
              value={selectedOptions}
              onChange={handleSelectionChange}
              placeholder="Select items for this group..."
              searchPlaceholder="Search items..."
              maxChips={3}
              size="sm"
              hideChips={true}
            />

            {/* Display selected items as badges */}
            {group.itemIds.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {group.itemIds.map((itemId) => {
                  const itemName = getItemDisplayName(itemId);
                  return (
                    <Badge
                      key={itemId}
                      variant="secondary"
                      className="text-xs px-2 py-1"
                    >
                      {itemName}
                    </Badge>
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function DimensionGroupingEditor({
  dimensionType,
  itemIdToName,
  groupingConfig,
  onConfigChange,
  onConfirm,
  onCancel,
}: DimensionGroupingEditorProps) {
  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Initialize with catch-all groups if missing
  const getInitialConfig = useCallback((): DimensionGroupingConfig => {
    const catchAllGroups =
      groupingConfig.catchAllGroups.length > 0
        ? groupingConfig.catchAllGroups
        : createDefaultCatchAllGroups(dimensionType);

    return {
      userGroups: groupingConfig.userGroups || [],
      catchAllGroups,
    };
  }, [groupingConfig, dimensionType]);

  // Track pending changes locally
  const [pendingConfig, setPendingConfig] =
    useState<DimensionGroupingConfig>(getInitialConfig());

  // Sync pending config when groupingConfig changes externally
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // On initial mount, use the initial config (which ensures catch-all groups exist)
      return;
    }
    // After initial mount, sync with groupingConfig, ensuring catch-all groups exist
    const catchAllGroups =
      groupingConfig.catchAllGroups.length > 0
        ? groupingConfig.catchAllGroups
        : createDefaultCatchAllGroups(dimensionType);
    setPendingConfig({
      userGroups: groupingConfig.userGroups || [],
      catchAllGroups,
    });
  }, [groupingConfig, dimensionType]);

  // Convert itemIdToName Map to MultiSelectOption[]
  const allOptions = useMemo(() => {
    const options: MultiSelectOption[] = [];

    // Add all regular items (catch-all groups don't need to be in options)
    itemIdToName.forEach((name, id) => {
      options.push({
        id,
        label: name,
      });
    });

    return options;
  }, [itemIdToName]);

  const handleCreateGroup = useCallback(() => {
    const newGroup: DimensionGroup = {
      id: `group-${Date.now()}`,
      name: `Group ${pendingConfig.userGroups.length + 1}`,
      itemIds: [],
    };
    const newConfig = {
      ...pendingConfig,
      userGroups: [...pendingConfig.userGroups, newGroup],
    };
    setPendingConfig(newConfig);
    // Don't call onConfigChange for new empty groups - wait until items are added
  }, [pendingConfig]);

  const handleUpdateItems = useCallback(
    (groupId: string, itemIds: string[]) => {
      // Only update user groups (catch-all groups don't have items)
      const updatedUserGroups = pendingConfig.userGroups.map((group) => {
        if (group.id === groupId) {
          return { ...group, itemIds };
        }
        return group;
      });
      const newConfig = {
        ...pendingConfig,
        userGroups: updatedUserGroups,
      };
      setPendingConfig(newConfig);
      // Only call onConfigChange if all groups have items (validation)
      const hasEmptyGroups = updatedUserGroups.some(
        (group) => group.itemIds.length === 0,
      );
      if (!hasEmptyGroups) {
        onConfigChange(newConfig);
      }
    },
    [pendingConfig, onConfigChange],
  );

  const handleRenameGroup = useCallback(
    (groupId: string, newName: string) => {
      // Check if it's a catch-all group
      const catchAllIndex = pendingConfig.catchAllGroups.findIndex(
        (g) => g.id === groupId,
      );
      let newConfig: DimensionGroupingConfig;
      if (catchAllIndex >= 0) {
        // Update catch-all group name
        const updatedCatchAllGroups = [...pendingConfig.catchAllGroups];
        updatedCatchAllGroups[catchAllIndex] = {
          ...updatedCatchAllGroups[catchAllIndex],
          name: newName,
        };
        newConfig = {
          ...pendingConfig,
          catchAllGroups: updatedCatchAllGroups,
        };
      } else {
        // Update user group name
        const updatedUserGroups = pendingConfig.userGroups.map((group) => {
          if (group.id === groupId) {
            return { ...group, name: newName };
          }
          return group;
        });
        newConfig = {
          ...pendingConfig,
          userGroups: updatedUserGroups,
        };
      }
      setPendingConfig(newConfig);
      onConfigChange(newConfig);
    },
    [pendingConfig, onConfigChange],
  );

  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      // Only delete user groups (catch-all groups cannot be deleted)
      const updatedUserGroups = pendingConfig.userGroups.filter(
        (group) => group.id !== groupId,
      );
      const newConfig = {
        ...pendingConfig,
        userGroups: updatedUserGroups,
      };
      setPendingConfig(newConfig);
      // If no user groups remain, clear the config entirely
      if (updatedUserGroups.length === 0) {
        onConfigChange(undefined);
        return;
      }
      // Only call onConfigChange if all remaining groups have items (validation)
      const hasEmptyGroups = updatedUserGroups.some(
        (group) => group.itemIds.length === 0,
      );
      if (!hasEmptyGroups) {
        onConfigChange(newConfig);
      }
    },
    [pendingConfig, onConfigChange],
  );

  const handleConfirm = useCallback(() => {
    // If no user groups, clear the config entirely (no grouping)
    if (pendingConfig.userGroups.length === 0) {
      onConfigChange(undefined);
      onConfirm?.();
      return;
    }
    // Double-check validation before confirming
    const hasEmptyGroups = pendingConfig.userGroups.some(
      (group) => group.itemIds.length === 0,
    );
    if (hasEmptyGroups) {
      return; // Don't save if there are empty groups
    }
    onConfigChange(pendingConfig);
    onConfirm?.();
  }, [pendingConfig, onConfigChange, onConfirm]);

  const handleCancel = useCallback(() => {
    // Reset to original config (if it was empty, it stays empty - default group only on initial open)
    setPendingConfig(groupingConfig);
    onCancel?.();
  }, [groupingConfig, onCancel]);

  const dimensionLabel = DIMENSION_LABELS[dimensionType];

  // Check if any user groups are empty (no items assigned)
  const hasEmptyGroups = useMemo(() => {
    return pendingConfig.userGroups.some((group) => group.itemIds.length === 0);
  }, [pendingConfig.userGroups]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{dimensionLabel} Grouping</Label>
      </div>

      {/* User Groups Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-muted-foreground">
            User Groups ({pendingConfig.userGroups.length})
          </Label>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCreateGroup}
            className="h-7"
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            New Group
          </Button>
        </div>

        {pendingConfig.userGroups.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                No user groups created. Create a group to start organizing
                items.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {pendingConfig.userGroups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                allOptions={allOptions}
                itemIdToName={itemIdToName}
                onUpdateItems={handleUpdateItems}
                onRenameGroup={handleRenameGroup}
                onDeleteGroup={handleDeleteGroup}
                dimensionType={dimensionType}
                isCatchAll={false}
              />
            ))}
          </div>
        )}
      </div>

      {/* Catch-All Groups Section */}
      <div className="space-y-4 pt-4 border-t">
        <Label className="text-sm font-medium text-muted-foreground">
          Catch-All Groups ({pendingConfig.catchAllGroups.length})
        </Label>
        <div className="space-y-3">
          {pendingConfig.catchAllGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              allOptions={allOptions}
              itemIdToName={itemIdToName}
              onUpdateItems={handleUpdateItems}
              onRenameGroup={handleRenameGroup}
              onDeleteGroup={handleDeleteGroup}
              dimensionType={dimensionType}
              isCatchAll={true}
            />
          ))}
        </div>
      </div>

      {(onConfirm || onCancel) && (
        <div className="space-y-2 pt-4 border-t">
          {hasEmptyGroups && (
            <p className="text-sm text-destructive">
              Please assign items to all groups before saving. Empty groups are
              not allowed.
            </p>
          )}
          <div className="flex justify-end gap-2">
            {onCancel && (
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            {onConfirm && (
              <Button onClick={handleConfirm} disabled={hasEmptyGroups}>
                Confirm
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
